import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { loadGuideline } from '../utils/guidelineLoader';
import { buildPageGraph, getPageList } from '../utils/graphUtils';

const GuidelineContext = createContext(null);

export function GuidelineProvider({ children }) {
  const { guidelineId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [guideline, setGuideline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const VALID_MODES = ['flowchart', 'walker'];
  const requestedMode = searchParams.get('mode');
  const mode = VALID_MODES.includes(requestedMode) ? requestedMode : 'flowchart';
  const requestedPageId = searchParams.get('page') || null;
  const selectedNodeId = searchParams.get('node') || null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadGuideline(guidelineId)
      .then((data) => {
        if (!cancelled) {
          setGuideline(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [guidelineId]);

  const pages = useMemo(() => (guideline ? getPageList(guideline) : []), [guideline]);

  // An unknown ?page= (e.g. a cross-guideline link) would otherwise render a
  // blank canvas with no explanation. Fall back to the first page and say so.
  const pageExists = requestedPageId && pages.some((p) => p.id === requestedPageId);
  const unknownPage = requestedPageId && pages.length > 0 && !pageExists;
  const activePage = pageExists
    ? requestedPageId
    : pages.length > 0
      ? pages[0].id
      : null;

  const { pageNodes, pageEdges } = useMemo(
    () =>
      guideline
        ? buildPageGraph(guideline.nodes, guideline.edges, activePage)
        : { pageNodes: [], pageEdges: [] },
    [guideline, activePage]
  );

  const setMode = useCallback((newMode) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('mode', newMode);
      return next;
    });
  }, [setSearchParams]);

  // `replace` for automatic page moves (walker auto-advance) so only
  // user-initiated navigation creates browser history entries.
  const setPage = useCallback((pageId, { replace = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', pageId);
      next.delete('node');
      return next;
    }, { replace });
  }, [setSearchParams]);

  const setSelectedNodeId = useCallback((nodeId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nodeId) next.set('node', nodeId);
      else next.delete('node');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const value = useMemo(() => ({
    guidelineId,
    guideline,
    loading,
    error,
    unknownPage,
    requestedPageId,
    validationErrors: guideline?.validationErrors || [],
    mode,
    setMode,
    pages,
    activePage,
    setPage,
    selectedNodeId,
    setSelectedNodeId,
    pageNodes,
    pageEdges,
    allNodes: guideline?.nodes || [],
    allEdges: guideline?.edges || [],
  }), [
    guidelineId, guideline, loading, error, unknownPage, requestedPageId,
    mode, setMode, pages, activePage, setPage, selectedNodeId,
    setSelectedNodeId, pageNodes, pageEdges,
  ]);

  return (
    <GuidelineContext.Provider value={value}>
      {children}
    </GuidelineContext.Provider>
  );
}

export function useGuideline() {
  const ctx = useContext(GuidelineContext);
  if (!ctx) throw new Error('useGuideline must be used within GuidelineProvider');
  return ctx;
}
